import React, { useMemo, useCallback, useEffect } from "react";

import {
  FormGroup,
  Label,
  Input,
  Row,
  Col,
} from 'reactstrap';

import Select from 'react-select';

import FieldError from '../../../../../helper/FieldError';

import WorkflowHttpRequestBodyRequestContentList from './content/list';
import WorkflowHttpRequestBodyRequestHeaderList from './header/list';

const WorkflowHttpRequestBodyRequestForm = props => {
  const {
    currentNode,
    callbacks: {
      onSetCurrentNode,
      onChangeCurrentNode,
      activeInputStageFileItems
    }
  } = props;

  // ---------- START REQUEST METHOD ----------
  const requestMethodOptions = [
    { value: 'get', label: 'GET' },
    { value: 'post', label: 'POST' },
    { value: 'put', label: 'PUT' },
    { value: 'delete', label: 'DELETE' },
    { value: 'patch', label: 'PATCH' },
  ]

  const requestMethodSelectedOption = useMemo(() => {
    return requestMethodOptions.find(option => option.value === currentNode?.data?.request_method)
  }, [currentNode?.data?.request_method])

  const requestMethodInput = () => {
    return(
      <FormGroup>
        <FieldError errors={ _.get(currentNode, 'data.errors.request_method') }>
          <Label>
            { I18n.t('activerecord.attributes.workflow/stage.request_method') }
          </Label>
          <Select
            value={ requestMethodSelectedOption }
            onChange={e => onChangeCurrentNode({ target: { value: e.value } }, 'data.request_method')}
            invalid={_.has(currentNode, 'data.errors.request_method')}
            options={ requestMethodOptions }
          />
        </FieldError>
      </FormGroup>
    )
  }
  // ----------------------------------------
  // ---------- START ENDPOINT URL ----------
  const endpointUrlInput = () => {
    return(
      <FormGroup>
        <FieldError errors={ _.get(currentNode, 'data.errors.endpoint_url') }>
          <Label>
            { I18n.t('activerecord.attributes.workflow/stage.endpoint_url') }
          </Label>
          <Input
            value={ _.get(currentNode, 'data.endpoint_url', '') }
            onChange={e => onChangeCurrentNode(e, 'data.endpoint_url')}
            invalid={_.has(currentNode, 'data.errors.endpoint_url')}
          />
        </FieldError>
      </FormGroup>
    )
  }
  // ---------- END ENDPOINT URL ----------
  // ----------------------------------------
  // ---------- START BODY REQUEST CONTENT TYPE ----------
  const bodyRequestContentTypeOptions = [
    { value: 'application/json', label: 'JSON' },
    // { value: 'xml', label: 'XML' },
    // { value: 'text', label: 'Text' },
    // { value: 'form-data', label: 'Form Data' },
    // { value: 'urlencoded', label: 'URL Encoded' },
  ]

  const bodyRequestContentTypeSelectedOption = useMemo(() => {
    return bodyRequestContentTypeOptions.find(option => option.value === currentNode?.data?.body_request_content_type)
  }, [currentNode?.data?.body_request_content])

  const bodyRequestContentTypeInput = () => {
    return(
      <FormGroup>
        <FieldError errors={ _.get(currentNode, 'data.errors.body_request_content_type') }>
          <Label>
            { I18n.t('activerecord.attributes.workflow/stage.body_request_content_type') }
          </Label>
          <Select
            value={ bodyRequestContentTypeSelectedOption }
            onChange={e => onChangeCurrentNode({ target: { value: e.value } }, 'data.body_request_content_type')}
            invalid={_.has(currentNode, 'data.errors.body_request_content_type')}
            options={ bodyRequestContentTypeOptions }
          />
        </FieldError>
      </FormGroup>
    )
  }
  // ---------- END BODY REQUEST CONTENT TYPE ----------
  // ----------------------------------------
  // ---------- START BODY REQUEST CONTENT ----------
  const onAddBodyRequestContentItem = useCallback(() => {
    const customID = `${ Math.random().toString(36).slice(2) }-${ moment().toDate().getTime() }-${ currentNode?.data?.body_request_content?.length }`;

    let bodyItem = {
      key: '',
      value: '',
      custom_id: customID
    }
    onSetCurrentNode(prevState => {
      return {
        ...prevState,
        data: {
          ...prevState.data,
          body_request_content: [
            ...prevState.data.body_request_content,
            bodyItem
          ]
        }
      }
    })
  }, [])

  const onRemoveBodyRequestContentItem = useCallback((customId) => {
    onSetCurrentNode(prevState => {
      return {
        ...prevState,
        data: {
          ...prevState.data,
          body_request_content: _.filter(prevState.data.body_request_content, item => item.custom_id != customId)
        }
      }
    })
  }, [])

  const onChangeBodyRequestContentItem = useCallback((event, customId) => {
    onSetCurrentNode(prevState => {
      return {
        ...prevState,
        data: {
          ...prevState.data,
          body_request_content: _.map(prevState.data.body_request_content, item => {
            if (item.custom_id == customId) {
              return {
                ...item,
                [event.target.name]: event.target.value
              }
            }
            return item
          })
        }
      }
    })
  }, [])

  useEffect(() => {
    if(_.isEmpty(currentNode?.data?.body_request_content)) {
      onAddBodyRequestContentItem()
    }
  }, [])

  const bodyRequestContentList = () => {
    return(
      <FormGroup>
        <Label>
          { I18n.t('activerecord.attributes.workflow/stage.body_request_content') }
        </Label>

        <WorkflowHttpRequestBodyRequestContentList
          currentNode={ currentNode }
          callbacks={{
            onAddBodyRequestContentItem: onAddBodyRequestContentItem,
            onRemoveBodyRequestContentItem: onRemoveBodyRequestContentItem,
            onChangeBodyRequestContentItem: onChangeBodyRequestContentItem,
            activeInputStageFileItems: activeInputStageFileItems
          }}
        />
      </FormGroup>
    )
  }
  // ---------- END BODY REQUEST CONTENT ----------
  // ----------------------------------------
  // ---------- START HEADER REQUEST CONTENT ----------
  const onAddHeaderRequestContentItem = useCallback(() => {
    const customID = `${ Math.random().toString(36).slice(2) }-${ moment().toDate().getTime() }-${ currentNode?.data?.header_request_content?.length }`;

    let headerItem = {
      key: '',
      value: '',
      custom_id: customID
    }
    onSetCurrentNode(prevState => {
      return {
        ...prevState,
        data: {
          ...prevState.data,
          header_request_content: [
            ...prevState.data.header_request_content,
            headerItem
          ]
        }
      }
    })
  }, [])

  const onRemoveHeaderRequestContentItem = useCallback((customId) => {
    onSetCurrentNode(prevState => {
      return {
        ...prevState,
        data: {
          ...prevState.data,
          header_request_content: _.filter(prevState.data.header_request_content, item => item.custom_id != customId)
        }
      }
    })
  }, [])

  const onChangeHeaderRequestContentItem = useCallback((event, customId) => {
    onSetCurrentNode(prevState => {
      return {
        ...prevState,
        data: {
          ...prevState.data,
          header_request_content: _.map(prevState.data.header_request_content, item => {
            if (item.custom_id == customId) {
              return {
                ...item,
                [event.target.name]: event.target.value
              }
            }
            return item
          })
        }
      }
    })
  }, [])

  useEffect(() => {
    if(_.isEmpty(currentNode?.data?.header_request_content)) {
      onAddHeaderRequestContentItem()
    }
  }, [])

  const headerRequestContentList = () => {
    return(
      <FormGroup>
        <Label>
          { I18n.t('activerecord.attributes.workflow/stage.header_request_content') }
        </Label>
        <WorkflowHttpRequestBodyRequestHeaderList
          currentNode={ currentNode }
          callbacks={{
            onAddHeaderRequestContentItem: onAddHeaderRequestContentItem,
            onRemoveHeaderRequestContentItem: onRemoveHeaderRequestContentItem,
            onChangeHeaderRequestContentItem: onChangeHeaderRequestContentItem
          }}
        />
      </FormGroup>
    )
  }
  // ---------- END HEADER REQUEST CONTENT ----------

  return (
    <Row>
      <Col md={ 12 }>
        { requestMethodInput() }
        { endpointUrlInput() }
        { bodyRequestContentTypeInput() }
        { headerRequestContentList() }
        { bodyRequestContentList() }
      </Col>
    </Row>
  )

}

export default WorkflowHttpRequestBodyRequestForm;
